@if (pageData$ | async; as pageData) {
  @if (pageData.quote && pageData.revision) {
    <div
      class="flex flex-col md:flex-row items-start justify-between mb-6 gap-4"
    >
      <div class="flex flex-col gap-2">
        <div class="font-semibold text-3xl whitespace-nowrap truncate">
          {{ pageData.quote.code_number }}-{{ pageData.revision.number }}
        </div>

        <div class="flex gap-2 items-center">
          @if (pageData.revision.published_at) {
            @if (pageData.quote.current_revision_id == pageData.revision.id) {
              <p-tag severity="success" class="text-xl">Published</p-tag>
            } @else {
              <p-tag severity="warn" class="text-xl">Superseded</p-tag>
            }
          } @else if (
            pageData.quote.current_revision_id == pageData.revision.id
          ) {
            <p-tag severity="secondary" class="text-xl">Unpublished</p-tag>
          }

          @if (pageData.quote.archived_at) {
            <p-tag severity="danger" class="text-xl">Archived</p-tag>
          }

          @if (isApprovalFlowEnabled) {
            @switch (pageData.revision.approval_status) {
              @case ("REQUESTED") {
                <p-tag severity="info" class="text-xl">Pending Review</p-tag>
              }
              @case ("APPROVED") {
                <p-tag severity="success" class="text-xl">Approved</p-tag>
              }
              @case ("CHANGES_REQUESTED") {
                <p-tag severity="warn" class="text-xl">Changes Requested</p-tag>
              }
            }
          }
        </div>
      </div>

      <div class="flex flex-wrap sm:justify-end gap-2">
        @if (isTradeStudyToolFeaturesEnabled) {
          <p-button
            severity="secondary"
            icon="pi pi-sliders-h"
            label="Trade Study Tools"
            (onClick)="tradeStudyMenu.toggle($event)"
          />

          <p-popover #tradeStudyMenu>
            <ng-template #content>
              <div class="flex flex-col gap-0 min-w-[220px]">
                <p-button
                  label="Layout"
                  icon="pi pi-sitemap"
                  styleClass="w-full"
                  severity="secondary"
                  [text]="true"
                  (onClick)="openLayoutDialog(); tradeStudyMenu.hide()"
                />

                <p-button
                  label="Charts"
                  icon="pi pi-chart-bar"
                  styleClass="w-full"
                  severity="secondary"
                  [text]="true"
                  (onClick)="openChartsDialog(); tradeStudyMenu.hide()"
                />
              </div>
            </ng-template>
          </p-popover>
        }
        <p-button
          severity="secondary"
          icon="pi pi-download"
          label="Download PDF"
          [disabled]="
            isApprovalFlowEnabled &&
            pageData.revision.approval_status !== 'APPROVED'
          "
          [pTooltip]="
            isApprovalFlowEnabled &&
            pageData.revision.approval_status !== 'APPROVED'
              ? 'Quote must be approved before downloading PDF'
              : ''
          "
          (onClick)="
            quotesService.downloadPdf(pageData.quote, pageData.revision)
          "
        />
        <app-quote-history [(visible)]="historyDrawerVisible" />
        <p-button
          severity="secondary"
          icon="pi pi-ellipsis-h"
          (onClick)="actionsMenu.toggle($event)"
        />
        <p-popover #actionsMenu>
          <ng-template #content>
            <div class="flex flex-col gap-0 min-w-[200px]">
              <p-button
                label="Download All CAD"
                icon="pi pi-download"
                styleClass="w-full"
                severity="secondary"
                [text]="true"
                (onClick)="downloadCADFiles(); actionsMenu.hide()"
                aria-label="Download All CAD Files"
              />
              @if (isPDFDrawingUploadEnabled) {
                <p-button
                  label="Download All Drawing Files"
                  icon="pi pi-file-pdf"
                  styleClass="w-full"
                  severity="secondary"
                  [text]="true"
                  (onClick)="downloadAllDrawingFiles(); actionsMenu.hide()"
                  aria-label="Download All Drawing Files"
                />
              }
              <p-button
                label="Download All Blocking Up"
                icon="pi pi-th-large"
                styleClass="w-full"
                severity="secondary"
                [text]="true"
                (onClick)="downloadAllBlockingUp(); actionsMenu.hide()"
                aria-label="Download All Blocking Up Files"
              />
              <p-button
                label="Download CSV"
                icon="pi pi-file-excel"
                styleClass="w-full"
                severity="secondary"
                [text]="true"
                (onClick)="downloadCSV(); actionsMenu.hide()"
                aria-label="Download CSV"
              />
              <p-button
                label="Set Currency"
                icon="pi pi-dollar"
                styleClass="w-full"
                severity="secondary"
                [text]="true"
                (onClick)="openCurrencyExchangeDialog(); actionsMenu.hide()"
                aria-label="Set currency and exchange rate"
              />
              <p-button
                label="Activity Log"
                icon="pi pi-history"
                styleClass="w-full"
                severity="secondary"
                [text]="true"
                (onClick)="openHistoryDrawer(); actionsMenu.hide()"
                aria-label="Open quote activity log"
              />
            </div>
          </ng-template>
        </p-popover>
      </div>
    </div>

    <!-- PAGE WARNINGS -->

    <div class="flex flex-col gap-2 mb-3">
      @if (
        isApprovalFlowEnabled &&
        pageData.revision.approval_status === "CHANGES_REQUESTED"
      ) {
        <div
          role="status"
          class="flex w-full items-start gap-3 rounded-xl border border-orange-400/40 bg-orange-500/5 px-4 py-4"
        >
          <i class="pi pi-comment mt-0.5 text-orange-500"></i>
          <div class="flex min-w-0 flex-col gap-2">
            <span class="text-sm font-semibold text-surface-switch-900"
              >Changes requested</span
            >
            @if (pageData.revision.approval_review_notes) {
              <p
                class="m-0 whitespace-pre-line break-words text-sm text-surface-switch-700"
              >
                {{ pageData.revision.approval_review_notes }}
              </p>
            }
            @if (
              pageData.revision.approval_reviewed_by ||
              pageData.revision.approval_reviewed_at
            ) {
              <span class="text-xs text-surface-switch-500">
                Requested
                @if (pageData.revision.approval_reviewed_by) {
                  by {{ pageData.revision.approval_reviewed_by }}
                }
                @if (pageData.revision.approval_reviewed_at) {
                  on
                  {{ pageData.revision.approval_reviewed_at | date: "medium" }}
                }
              </span>
            }
            @if (
              canRequestReview &&
              isViewingCurrentRevision(pageData.quote, pageData.revision)
            ) {
              <span class="text-sm text-surface-switch-600"
                >Update this revision, then re-request review.</span
              >
            }
          </div>
        </div>
      }

      @if (pageData.quote.current_revision_id !== pageData.revision.id) {
        <div
          role="status"
          class="flex w-full items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3"
        >
          <i class="pi pi-lock mt-0.5 text-primary"></i>
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-semibold text-surface-switch-900"
              >You're viewing an older revision</span
            >
            <span class="text-sm text-surface-switch-600"
              >This revision is read-only. Go to the
              <button
                type="button"
                class="cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-primary underline hover:no-underline"
                (click)="
                  openCurrentRevision(pageData.quote, pageData.revisions)
                "
              >
                current revision
              </button>
              to make changes.</span
            >
          </div>
        </div>
      }
    </div>

    <!-- INFO CARD -->

    <app-quote-info
      class="block mb-3"
      [readOnly]="!isViewingCurrentRevision(pageData.quote, pageData.revision)"
    />

    <p-card
      class="rounded-xl border border-surface-switch-300 bg-surface-switch-0 overflow-hidden mb-3"
    >
      <ng-template #header>
        <div
          class="flex justify-between items-center gap-4 p-5 pt-4 pb-0 w-full"
        >
          <div class="font-semibold text-xl">Item List</div>
          @if (isViewingCurrentRevision(pageData.quote, pageData.revision)) {
            <div
              class="flex items-center flex-nowrap justify-end gap-3"
              [class.invisible]="selectedRevisionItemCount === 0"
              [attr.aria-hidden]="selectedRevisionItemCount === 0"
            >
              <span class="text-sm text-color-secondary font-medium">
                {{ selectedRevisionItemCount }}
                {{ selectedRevisionItemCount === 1 ? "item" : "items" }}
                selected
              </span>
              <p-button
                label="Edit"
                icon="pi pi-pencil"
                size="small"
                (onClick)="openRevisionItemBulkEditDialog()"
              />
              <p-button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                size="small"
                (onClick)="openRevisionItemBulkDeleteDialog()"
              />
            </div>
          }
        </div>
      </ng-template>

      @if (quoteRevisionItemService.error$ | async; as revisionItemError) {
        <div
          class="mx-3 mb-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700"
        >
          {{ revisionItemError }}
        </div>
      }
      @if (quoteRevisionItemService.items$ | async; as quoteRevisionItems) {
        <div
          #revisionItemListScroll
          class="@container item-list-scroll overflow-x-auto overflow-y-hidden -mx-2 -mt-2"
          [attr.inert]="quoteRevisionItemService.loading() ? '' : null"
          [attr.aria-busy]="quoteRevisionItemService.loading()"
          (scroll)="onItemListScroll($event)"
        >
          <div [class]="itemListMinWidthClass">
            <div [class]="itemListHeaderClass">
              <div class="col-span-1 min-w-0 flex items-center justify-start">
                @if (
                  isViewingCurrentRevision(pageData.quote, pageData.revision)
                ) {
                  <div
                    class="inline-flex items-center justify-center min-w-8 min-h-8 ml-3"
                  >
                    <p-checkbox
                      [ngModel]="
                        areAllRevisionItemsSelected(quoteRevisionItems)
                      "
                      (ngModelChange)="
                        toggleSelectAllRevisionItems(quoteRevisionItems)
                      "
                      [binary]="true"
                      aria-label="Select all revision items"
                    />
                  </div>
                }
              </div>
              <div
                class="col-span-3 flex items-center py-3 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
              >
                Item
              </div>
              <div
                class="relative col-span-1 flex items-center py-3 pl-4 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
              >
                <div
                  class="absolute inset-y-4 left-0 w-px bg-surface-switch-300"
                ></div>
                Pricing Model
              </div>
              <div
                class="col-span-1 flex items-center py-3 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
              >
                Material
              </div>
              <div
                class="col-span-1 flex items-center py-3 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
              >
                Quantity
              </div>
              <div
                class="relative col-span-1 flex items-center py-3 pl-4 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
              >
                <div
                  class="absolute inset-y-4 left-0 w-px bg-surface-switch-300"
                ></div>
                Unit Cost
              </div>
              @if (!isTradeStudyToolFeaturesEnabled) {
                <div
                  class="col-span-1 flex items-center py-3 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
                >
                  Unit Markup
                </div>
              }
              <div
                class="col-span-1 flex items-center py-3 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
              >
                Unit Price
              </div>
              @if (isConsumablesEnabled) {
                <div
                  class="relative col-span-1 flex items-center py-3 pl-4 pr-2 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
                >
                  <div
                    class="absolute inset-y-4 left-0 w-px bg-surface-switch-300"
                  ></div>
                  Consumables
                </div>
              }
              <div
                class="relative col-span-1 flex items-center py-3 pl-4 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
              >
                <div
                  class="absolute inset-y-4 left-0 w-px bg-surface-switch-300"
                ></div>
                Subtotal
              </div>
              @if (isTradeStudyToolFeaturesEnabled) {
                <div
                  class="col-span-1 flex items-center py-3 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
                >
                  CapEx
                </div>
              }
              @if (!isTradeStudyToolFeaturesEnabled) {
                <div
                  class="col-span-1 flex items-center py-3 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
                >
                  Tax
                </div>
              }
              <div
                class="item-list-sticky-end item-list-sticky-end--header relative col-span-2 flex items-center bg-inherit py-3 pr-0 pl-4"
              >
                <div
                  class="absolute inset-y-4 left-0 w-px bg-surface-switch-300"
                ></div>
                <div
                  class="flex-1 min-w-0 text-left text-xs font-medium uppercase tracking-wider text-surface-switch-600"
                >
                  Total
                </div>
                <div class="shrink-0 min-w-[80px]" aria-hidden="true"></div>
              </div>
            </div>
            <div
              class="flex flex-col pb-1"
              [class.dragging-active]="
                quoteRevisionItemService.dragging$ | async
              "
            >
              @for (
                quoteRevisionItem of quoteRevisionItems;
                track quoteRevisionItem.persistent_id
              ) {
                <div
                  pDroppable
                  (drop)="
                    isViewingCurrentRevision(
                      pageData.quote,
                      pageData.revision
                    ) &&
                      dropRevisionItemRootBefore(
                        quoteRevisionItem.persistent_id
                      )
                  "
                  class="drop-line"
                ></div>
                <app-quote-revision-item
                  [item]="quoteRevisionItem"
                  [currencyCode]="pageData.revision.currency_code"
                  [tradeStudyToolFeaturesEnabled]="
                    isTradeStudyToolFeaturesEnabled
                  "
                  [consumablesEnabled]="isConsumablesEnabled"
                  [selectedPersistentIds]="selectedRevisionItemPersistentIds"
                  [readOnly]="
                    !isViewingCurrentRevision(pageData.quote, pageData.revision)
                  "
                  (itemSelectionChange)="
                    setRevisionItemSelected(
                      $event.persistentId,
                      $event.selected
                    )
                  "
                  (addItemRequested)="openRevisionItemAddDialog($event)"
                />
                @if ($last) {
                  <div
                    pDroppable
                    (drop)="
                      isViewingCurrentRevision(
                        pageData.quote,
                        pageData.revision
                      ) && dropRevisionItemRootAtEnd()
                    "
                    class="drop-line"
                  ></div>
                }
              } @empty {
                <p class="py-6 px-3 text-sm text-surface-switch-600 m-0">
                  No items added yet.
                </p>
              }
            </div>
          </div>
        </div>
        <div class="border-t border-surface-switch-200">
          @if (isViewingCurrentRevision(pageData.quote, pageData.revision)) {
            <p-button
              icon="pi pi-plus"
              label="Add New Item"
              [outlined]="true"
              styleClass="w-full mt-4 !border-primary-200 !text-primary hover:!bg-primary-50 hover:!border-primary-300 gap-2"
              severity="secondary"
              (onClick)="openRevisionItemAddDialog(null)"
            />
          }
        </div>
      } @else {
        <p class="p-4 text-sm text-surface-switch-600">
          Loading revision items…
        </p>
      }
    </p-card>

    <!-- Add Item Dialog - placed outside all cards -->
    <app-quote-revision-item-add
      [(visible)]="revisionItemAddDialogVisible"
      [parentQuoteItemPersistentId]="revisionItemAddParentPersistentId"
      [currencyCode]="pageData.revision.currency_code"
      [tradeStudyToolFeaturesEnabled]="isTradeStudyToolFeaturesEnabled"
      [consumablesEnabled]="isConsumablesEnabled"
      [pdfDrawingUploadEnabled]="isPDFDrawingUploadEnabled"
    ></app-quote-revision-item-add>

    @if (quoteRevisionItemService.items$ | async; as revisionItemsForBulk) {
      <app-quote-revision-item-bulk-edit-dialog
        [(visible)]="revisionItemBulkEditDialogVisible"
        [selectedItems]="getSelectedRevisionItems(revisionItemsForBulk)"
        [tradeStudyToolFeaturesEnabled]="isTradeStudyToolFeaturesEnabled"
        (saved)="onRevisionItemBulkEditSaved()"
      />

      <app-quote-revision-item-bulk-delete-dialog
        [(visible)]="revisionItemBulkDeleteDialogVisible"
        [items]="revisionItemsForBulk"
        [selectedPersistentIds]="selectedRevisionItemPersistentIds"
        (deleted)="onRevisionItemBulkDeleteDeleted()"
      />
    }

    <div class="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr]">
      <app-quote-revisions
        class="block min-w-0"
        [quoteCode]="pageData.quote.code_number"
        [revisions]="pageData.revisions ?? []"
        [viewingRevisionNumber]="pageData.revision.number"
        [currentRevisionId]="pageData.quote.current_revision_id"
        [approvalFlowEnabled]="isApprovalFlowEnabled"
        [canSubmit]="canRequestReview"
        [canApprove]="canApproveOrRequestChanges"
      />

      <p-card
        class="w-full overflow-hidden rounded-xl border border-surface-switch-300 bg-white"
      >
        <ng-template #header>
          <div class="flex items-center justify-between p-5 pt-4 pb-0">
            <div class="font-semibold text-xl text-left">Payment</div>
            <button
              pButton
              [text]="true"
              size="large"
              severity="secondary"
              [disabled]="
                !isViewingCurrentRevision(pageData.quote, pageData.revision)
              "
              (click)="openShippingPackagingDrawer()"
            >
              <i class="pi pi-pencil w-4 h-4" pButtonIcon></i>
            </button>
          </div>
        </ng-template>

        <div class="flex w-full flex-col gap-2 text-left">
          <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4">
            <span class="text-sm font-medium text-surface-switch-900"
              >Items</span
            >
            <span
              class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-900"
            >
              {{
                quoteService.revision.items_price
                  | currency: quoteService.revision.currency_code
              }}
            </span>
          </div>
          <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4 pl-4">
            <span class="text-sm text-surface-switch-600">Items Cost</span>
            <span
              class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-600"
            >
              {{
                quoteService.revision.items_cost
                  | currency: quoteService.revision.currency_code
              }}
            </span>
          </div>
          @if (!isTradeStudyToolFeaturesEnabled) {
            <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4 pl-4">
              <span class="text-sm text-surface-switch-600">Items Markup</span>
              <span
                class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-600"
              >
                {{
                  quoteService.revision.items_markup
                    | currency: quoteService.revision.currency_code
                }}
              </span>
            </div>
          }
          @if (isTradeStudyToolFeaturesEnabled) {
            <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4 pl-4">
              <span class="text-sm text-surface-switch-600">Items CapEx</span>
              <span
                class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-600"
              >
                {{
                  quoteService.revision.items_capex
                    | currency: quoteService.revision.currency_code
                }}
              </span>
            </div>
          }
          @if (isConsumablesEnabled) {
            <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4 pl-4">
              <span class="text-sm text-surface-switch-600"
                >Items Consumables</span
              >
              <span
                class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-600"
              >
                {{
                  quoteService.revision.items_consumables
                    | currency: quoteService.revision.currency_code
                }}
              </span>
            </div>
          }
          <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4">
            <span class="text-sm font-semibold text-surface-switch-600"
              >Shipping</span
            >
            <span
              class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-900"
            >
              {{
                quoteService.revision.shipping_price
                  | currency: quoteService.revision.currency_code
              }}
            </span>
          </div>
          <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4">
            <span class="text-sm font-semibold text-surface-switch-600"
              >Packaging</span
            >
            <span
              class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-900"
            >
              {{
                quoteService.revision.packaging_price
                  | currency: quoteService.revision.currency_code
              }}
            </span>
          </div>
          <p-divider class="my-4"></p-divider>
          <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4">
            <span class="text-base font-medium text-surface-switch-700"
              >Subtotal</span
            >
            <span
              class="min-w-[7rem] text-right text-base font-medium tabular-nums text-surface-switch-900"
            >
              {{
                quoteService.revision.subtotal
                  | currency: quoteService.revision.currency_code
              }}
            </span>
          </div>
          @if (!isTradeStudyToolFeaturesEnabled) {
            <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4">
              <span class="text-sm text-surface-switch-600">Tax</span>
              <span
                class="min-w-[7rem] text-right text-sm tabular-nums text-surface-switch-600"
                >{{
                  quoteService.revision.total_tax
                    | currency: quoteService.revision.currency_code
                }}</span
              >
            </div>
          }

          <p-divider class="my-4"></p-divider>

          <div class="grid grid-cols-[1fr_auto] items-baseline gap-x-4">
            <span class="text-xl font-semibold text-surface-switch-900"
              >Total</span
            >
            <span
              class="min-w-[7rem] text-right text-xl font-bold tabular-nums text-surface-switch-900"
            >
              {{
                quoteService.revision.total
                  | currency: quoteService.revision.currency_code
              }}
            </span>
          </div>
        </div>
      </p-card>
    </div>

    <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-wrap items-center gap-3">
        @if (pageData.quote.archived_at === null) {
          <p-button
            severity="danger"
            label="Archive Quote"
            icon="pi pi-inbox"
            outlined
            [disabled]="
              !isViewingCurrentRevision(pageData.quote, pageData.revision)
            "
            (click)="archiveQuote()"
          />
        } @else {
          <p-button
            severity="primary"
            label="Unarchive Quote"
            icon="pi pi-inbox"
            outlined
            [disabled]="
              !isViewingCurrentRevision(pageData.quote, pageData.revision)
            "
            (click)="unarchiveQuote()"
          />
        }
        <p-button
          severity="danger"
          label="Delete Quote"
          icon="pi pi-trash"
          outlined
          [disabled]="
            !isViewingCurrentRevision(pageData.quote, pageData.revision)
          "
          (click)="confirmDeleteQuote()"
        />
      </div>

      <p-button
        severity="secondary"
        icon="pi pi-download"
        label="Download PDF"
        [disabled]="
          isApprovalFlowEnabled &&
          pageData.revision.approval_status !== 'APPROVED'
        "
        [pTooltip]="
          isApprovalFlowEnabled &&
          pageData.revision.approval_status !== 'APPROVED'
            ? 'Quote must be approved before downloading PDF'
            : ''
        "
        (onClick)="quotesService.downloadPdf(pageData.quote, pageData.revision)"
      />
    </div>
    <shared-confirm-dialog />

    <app-shipping-packaging-edit />
    <app-currency-exchange-edit />

    <app-trade-study-layout-dialog
      [(visible)]="layoutDialogVisible"
      [quoteId]="pageData.quote.id"
      (saved)="onTradeStudyLayoutSaved()"
    />
    <app-trade-study-charts-dialog
      [(visible)]="chartsDialogVisible"
      [quoteId]="pageData.quote.id"
      [quoteDisplayCode]="
        pageData.quote.code_number + '-' + pageData.revision.number
      "
    />
  }
}
